import {isPlatformBrowser} from '@angular/common';
import {computed, inject, Injectable, PLATFORM_ID, Signal, signal, WritableSignal} from '@angular/core';
import {ActivatedRouteSnapshot, Router} from '@angular/router';
import {APP_ENVIRONMENT} from '@app/core/tokens/app-environment.token';
import {APP_SIGNALS} from '@app/core/tokens/app-signals.token';
import {
  type ConfigurationResponse,
  type GetTokenHeaders,
  type GetTokenParams,
  LoginService,
  OrganizationService,
} from '@middleware';
import {LocalStorageService} from '@services/browser/local-storage.service';
import {WindowLocationService} from '@services/browser/window-location.service';
import {EnvConfigService} from '@services/config/env-config.service';
import {useDummyUser} from '@utilities/user-mode.util';
import {SsrCookieService as CookieService} from 'ngx-cookie-service-ssr';
import {lastValueFrom} from 'rxjs';

/**
 * Manages the authentication state for the application, handling tokens in
 * cookies and local storage, and providing accessors as Angular Signals.
 * It integrates with the middleware for login, logout, and token refresh.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private tokenExpiryTimeout: any;
  private _issued: Date = undefined;
  private readonly _token!: WritableSignal<string>;
  private readonly _isBrowser: boolean;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly appSignals = inject(APP_SIGNALS);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly hostname: Signal<string>;

  constructor(
    public router: Router,
    public cookieService: CookieService,
    public mwAuthService: LoginService,
    public mwOrgService: OrganizationService,
    public envConfigService: EnvConfigService,
    public windowLocationService: WindowLocationService,
    public localStorageService: LocalStorageService,
  ) {
    this._isBrowser = isPlatformBrowser(this.platformId);
    this.hostname = this.appSignals.hostname;

    let initialToken: string | null = null;

    if (this._isBrowser) {
      initialToken = this.localStorageService.getItem('token');
      if (initialToken) {
        this.cookieService.set('token', initialToken, {
          expires: this.tokenExpiryTimeout,
          path: '/',
          domain: this.hostname(),
          secure: true,
          sameSite: 'Strict',
        });
      } else {
        initialToken = this.cookieService.get('token');
        if (initialToken) {
          this.localStorageService.setItem('token', initialToken);
        }
      }
    } else {
      initialToken = this.cookieService.get('token');
    }

    this._token = signal<string>(initialToken || undefined);

    if (initialToken) {
      this.scheduleTokenExpiryCheck();
    }
  }

  /** Begin login: store target URL, then navigate to session route */
  public async beginLogin(provider: string): Promise<void> {
    let targetUrl = '/';
    let nav = this.router.lastSuccessfulNavigation();
    let envConfig: ConfigurationResponse | null = null;
    envConfig = await this.envConfigService.getEnvConfig();

    // Walk back navigation stack until we find a "real" route
    while (
      nav != null &&
      ['login', 'logout', 'session', 'timedout'].some(v => {
        return nav['targetBrowserUrl']?.toString().includes(v) || nav.extractedUrl.toString().includes(v);
      })
    ) {
      nav = nav.previousNavigation;
    }
    if (nav != null) {
      targetUrl = nav['targetBrowserUrl']?.toString() ?? nav.extractedUrl.toString();
    }

    this.localStorageService.setItem('prev_url', targetUrl);

    if (envConfig?.use_header_auth) {
      // Header-auth flow: Angular session route
      await this.router.navigate(['/session', provider]);
    } else {
      // SAML/Shibboleth still needs a real redirect
      this.windowLocationService.redirectTo(
        `https://${this.hostname()}/Shibboleth.sso/Login?target=/session/${provider}&entityID=${provider}`,
      );
    }
  }

  /** Complete login fully inside Angular */
  public async completeLogin(redirectData: ActivatedRouteSnapshot): Promise<string> {
    const idp = redirectData.params['idp'];

    const organizations = (await lastValueFrom(this.mwOrgService.getOrganizations())).organizations;
    const org = organizations.find(o => o.entity_id === idp);
    const idpName = org?.name;

    let targetUrl = this.localStorageService.getItem('prev_url') ?? '/';
    this.localStorageService.removeItem('prev_url');

    if (!idp || !idpName) return targetUrl;

    const loginParams: GetTokenParams = {
      organization: idpName,
    };
    const loginHeaders: GetTokenHeaders = {
      eppn: 'no_eppn',
    };

    let envConfig: ConfigurationResponse | null = null;
    envConfig = await this.envConfigService.getEnvConfig();

    if (envConfig?.use_header_auth) {
      useDummyUser(this.environment, org, loginParams, loginHeaders);
    } else {
      // The `eppn` header must be stripped when logging in for real (non-local / dummy user)
      delete loginHeaders.eppn;
    }

    let token: string | undefined;
    try {
      token = this.stripToken(await lastValueFrom(this.mwAuthService.getToken(loginParams, loginHeaders)));
    } catch (e) {
      console.error(e);
      return targetUrl;
    }

    if (token) {
      this._setAuthCookies(token, idp);

      this.localStorageService.setItem('token', token);
      this.localStorageService.setItem('idp', idp);
      this._token.set(token);

      this.scheduleTokenExpiryCheck();
    }

    if (org?.id) {
      const [path, query] = targetUrl.split('?');
      const params = new URLSearchParams(query);
      if (path === '/resource-explorer/search' && !params.has('audience')) {
        params.set('audience', org.id);
        targetUrl = `${path}?${params.toString()}`;
      }
    }

    return targetUrl;
  }

  public beginLogout(): void {
    this.completeLogout();
  }

  public completeLogout(redirectPath: string = '/logout'): void {
    if (this.tokenExpiryTimeout) {
      clearTimeout(this.tokenExpiryTimeout);
      this.tokenExpiryTimeout = null;
    }

    this._issued = undefined;
    this.localStorageService.clear();

    if (this._isBrowser) {
      const cookieDomain = location.hostname;
      const isSecure = location.protocol.endsWith('s');

      this.cookieService.delete('token', '/', cookieDomain, isSecure, 'Strict');
      this.cookieService.delete('idp', '/', cookieDomain, isSecure, 'Strict');
    } else {
      this.cookieService.delete('token', '/');
      this.cookieService.delete('idp', '/');
    }

    this._token.set(undefined);

    // Angular navigation if internal
    if (redirectPath.startsWith('/')) {
      this.router.navigate([redirectPath]);
    } else {
      // External redirect (if ever needed)
      this.windowLocationService.redirectTo(redirectPath);
    }
  }

  /** Token helpers */
  private _setAuthCookies(token: string, idp: string): void {
    if (this._isBrowser) {
      // location is only available in the browser
      const cookieDomain = location.hostname;
      const isSecure = location.protocol.endsWith('s');

      const expiryDate = this._expires(token);

      this.cookieService.set('token', token, {
        expires: expiryDate,
        path: '/',
        domain: cookieDomain,
        secure: isSecure,
        sameSite: 'Strict',
      });
      this.cookieService.set('idp', idp, {
        expires: expiryDate,
        path: '/',
        domain: cookieDomain,
        secure: isSecure,
        sameSite: 'Strict',
      });
    }
  }

  public get token(): Signal<string> {
    return this._token.asReadonly();
  }

  private _expires(token: string): Date {
    try {
      if (token.includes('.eyJ')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload['exp'];
        if (typeof exp === 'number') return new Date(exp * 1000);
        console.warn('Token missing exp:', payload);
      }
    } catch (error) {
      console.error('Failed parsing token:', error);
    }

    if (!this._issued) this._issued = new Date();
    return new Date(this._issued.getTime() + 60000); //Default exp time is one minute
  }

  public expires: Signal<Date> = computed(() => {
    if (!this.token()) return new Date(0);
    return this._expires(this.token());
  });

  public scheduleTokenExpiryCheck(): void {
    const token = this.token();
    if (!token) return;

    if (!this._isBrowser) return;

    const expiry = this._expires(token);
    const delay = expiry.getTime() - Date.now();

    if (Number.isNaN(delay) || delay <= 0) {
      this.completeLogout('/timedout');
      return;
    }

    if (this.tokenExpiryTimeout) clearTimeout(this.tokenExpiryTimeout);

    this.tokenExpiryTimeout = setTimeout(() => {
      this.checkTokenExpiration();
    }, delay);
  }

  public checkTokenExpiration(): void {
    if (new Date() >= this.getTokenExpiry()) {
      this.completeLogout('/timedout');
    }
  }

  public getTokenExpiry(): Date | null {
    const token = this.token();
    if (!token) return null;
    return this._expires(token);
  }

  public async extendSession(): Promise<boolean> {
    try {
      const idp = this.localStorageService.getItem('idp') ?? this.cookieService.get('idp');
      if (!idp) return false;
      const org = (await lastValueFrom(this.mwOrgService.getOrganizations())).organizations.find(
        org => org.entity_id == idp,
      );
      const idpName = org?.name;
      if (!idpName) return false;

      const loginParams: GetTokenParams = {
        organization: idpName,
      };
      const loginHeaders: GetTokenHeaders = {
        eppn: 'no_eppn',
      };

      let envConfig: ConfigurationResponse | null = null;
      envConfig = await this.envConfigService.getEnvConfig();

      if (envConfig?.use_header_auth) {
        useDummyUser(this.environment, org, loginParams, loginHeaders);
      }

      let token: string = undefined;
      token = this.stripToken(await lastValueFrom(this.mwAuthService.getToken(loginParams, loginHeaders)));

      this._issued = new Date();
      if (token) {
        this._setAuthCookies(token, idp);

        this.localStorageService.setItem('token', token);
        this.localStorageService.setItem('idp', idp);
        this._token.set(token);
        this.scheduleTokenExpiryCheck();
        return true;
      }
    } catch (e) {
      console.error('Error extending session:', e);
    }
    return false;
  }

  public isLoggedIn(): boolean {
    const current = new Date();
    return current <= this.getTokenExpiry();
  }

  stripToken(s?: string) {
    if (!s) return '';

    // Strip whitespace and quote characters from the token, if present.
    return s.replace(/["'\s\n\r]+/g, '');
  }
}
